import { useCallback, useMemo } from 'react';
import {
  AppHeader,
  Button,
  ContextualMenu,
  IconButton,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { IconGlobe } from '@adc/parallax-icons';

import { useAuthState } from '~/features/auth/useAuthState';
import { useModal } from '~/features/modals/useModal';
import { useNavigationItems } from '~/hooks/useNavigationItems';
import { buildTestId } from '~/utils/buildTestId';
import { toCapitalAcronym } from '~/utils/toCapitalAcronym';

import { AppMenuItemButton } from './AppMenuItemButton';

const TEST_ID = 'header' as const;

export const Header = () => {
  const { isAuthenticated, userInfo } = useAuthState();
  const { openModal } = useModal();
  const { appMenuItems, handleSelectAppMenuItem, appMenuItemSelected } =
    useNavigationItems();

  const { id: selectedMenuItemId, label: selectedMenuItemLabel } =
    appMenuItems[appMenuItemSelected] ?? {};

  const openSettingsModal = useCallback(() => {
    openModal('Settings');
  }, [openModal]);

  const headerButtons = useMemo(
    () => [
      isAuthenticated ? (
        <IconButton
          key="settings"
          aria-label="Settings"
          onPress={openSettingsModal}
          size="M"
          variant="primary"
          // @ts-expect-error Design size is not compatible with IconButton size
          width={44}
          marginRight="$2"
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['settings', 'button'],
          })}
        >
          <Text color="$surface.brand" type="$bodyBase.medium">
            {toCapitalAcronym(`${userInfo?.firstName} ${userInfo?.lastName}`)}
          </Text>
        </IconButton>
      ) : (
        <Button
          variant="tertiary"
          key="language"
          size="medium"
          aria-label="Language"
          label="English"
          iconStart={<IconGlobe />}
          // TODO: implement language selection and remove setting modal trigger
          onPress={openSettingsModal}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['language', 'button'],
          })}
        />
      ),
    ],
    [isAuthenticated, openSettingsModal, userInfo],
  );

  return (
    <AppHeader
      testID={TEST_ID}
      title={
        <Stack flexDirection="row" alignItems="center" minHeight={50}>
          {appMenuItems.length > 0 ? (
            <ContextualMenu
              items={appMenuItems}
              selectedIndex={appMenuItemSelected}
              maxWidth={400}
              placement="trailing"
              onSelect={handleSelectAppMenuItem}
              testID={buildTestId({
                testID: TEST_ID,
                suffix: 'menu',
              })}
            >
              <AppMenuItemButton
                testID={buildTestId({
                  testID: TEST_ID,
                  suffix: ['menu', 'button'],
                })}
              />
            </ContextualMenu>
          ) : (
            <AppMenuItemButton
              hideSelectMenu
              testID={buildTestId({
                testID: TEST_ID,
                suffix: ['menu', 'button'],
              })}
            />
          )}

          <Stack>
            <Text
              type="$h3.strong"
              color="$neutral.noChangeWhite"
              testID={buildTestId({
                testID: TEST_ID,
                suffix: 'title',
              })}
            >
              Lactate<Text type="$h3.default">View</Text>
            </Text>
            {selectedMenuItemLabel && selectedMenuItemId ? (
              <Text
                type="$bodyBase.default"
                color="$neutral.noChangeWhite"
                testID={buildTestId({
                  testID: TEST_ID,
                  suffix: ['selected', 'item', selectedMenuItemId],
                })}
              >
                {selectedMenuItemLabel}
              </Text>
            ) : null}
          </Stack>
        </Stack>
      }
      variant="primary"
      buttons={headerButtons}
    />
  );
};
